import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ToastContext } from "../../../../hooks/context";

import { useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import { cleanObject, extractDateOnly } from "../../../../helpers";
import {
  useCreatePOMutation,
  useUpdatePOMutation,
} from "../../../../hooks/api/mutations";

export const usePurchaseOrderForm = ({
  isEditMode = false,
  PODetails = {},
  POItems = [],
}) => {
  const triggerToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { storeList } = useSelector((state) => state.profileDetailsReducer);

  const [pOForm, setPOForm] = useState({
    supplier_id: PODetails?.supplier?.id || "",
    supplier_name: PODetails?.supplier?.name || "",
    category_id: PODetails?.category?.id || "",
    store_id: PODetails?.store?.id || "",
    po_date: PODetails?.po_date
      ? extractDateOnly(PODetails?.po_date)
      : extractDateOnly(new Date()),
    notes: PODetails?.notes || "",
    shipping_details: {
      address: PODetails?.shipping_details?.address || "",
      name: PODetails?.shipping_details?.name || "",
      phone_code: PODetails?.shipping_details?.phone_code || "+234",
      phone_number: PODetails?.shipping_details?.phone_number || "",
      delivery_date: PODetails?.shipping_details?.delivery_date
        ? extractDateOnly(PODetails?.shipping_details?.delivery_date)
        : "",
    },
    items:
      POItems?.length > 0
        ? POItems.map((item) => ({
            ...item,
            base_cost_price: item.base_cost_price ?? item.cost_price ?? 0,
            selling_price: item.selling_price ?? 0,
            base_selling_price:
              item.base_selling_price ?? item.selling_price ?? 0,
            percentage: item.percentage ?? "",
          }))
        : [],
  });

  useEffect(() => {
    if (!PODetails?.shipping_details?.address && pOForm.store_id) {
      const selectedStore = storeList?.find(
        (store) => `${store?.id}` === `${pOForm.store_id}`,
      );
      if (selectedStore?.address) {
        setPOForm((prev) => ({
          ...prev,
          shipping_details: {
            ...prev.shipping_details,
            address: selectedStore.address,
          },
        }));
      }
    }
  }, [pOForm.store_id]);

  const { mutate: createPO, isLoading: isCreatingPO } = useCreatePOMutation();
  const { mutate: UpdatePO, isLoading: isUpdatingPO } = useUpdatePOMutation({
    id: PODetails?.id,
  });

  const getDataForSubmission = () => {
    return cleanObject(
      {
        ...pOForm,
        shipping_details: cleanObject({
          ...pOForm?.shipping_details,
          phone_code:
            pOForm?.shipping_details?.phone_number !== "" ? "+234" : "",
        }),
        items: pOForm?.items.map((item) => {
          const itemPayload = {
            product_id: item?.product_id,
            variant_id: item?.variant_id || null,
            requested_quantity:
              item?.requested_quantity !== "" &&
              item?.requested_quantity !== undefined
                ? Number(item?.requested_quantity)
                : 0,
            cost_price:
              item?.cost_price !== "" && item?.cost_price !== undefined
                ? Number(item?.cost_price)
                : 0,
            vat_rate:
              item?.vat_rate !== "" && item?.vat_rate !== undefined
                ? Number(item?.vat_rate)
                : 0,
            store_id: item?.store_id || pOForm?.store_id || null,
          };
          if (item?.selling_price !== undefined && item?.selling_price !== "") {
            itemPayload.selling_price = Number(item?.selling_price);
          }
          return itemPayload;
        }),
      },
      ["store_id"],
    );
  };

  const handleCreatePO = (status) => {
    if (!pOForm?.items || pOForm?.items.length === 0)
      return triggerToast("Please add items to the Purchase Order", "warning");

    const data = getDataForSubmission();

    createPO(
      { ...data, status },
      {
        onSuccess: (res) => {
          triggerToast(
            "Your Purchase Order has successfully been created",
            "success",
          );

          navigate(
            pathConstants.INVENTORY_PURCHASE_ORDER_DETAILS({
              id: res?.data?.data?.id,
            }),
          );
        },
      },
    );
  };

  const handleUpdatePO = (status) => {
    if (!pOForm?.items || pOForm?.items.length === 0)
      return triggerToast("Please add items to the Purchase Order", "warning");

    const data = getDataForSubmission();

    UpdatePO(
      { id: PODetails?.id, data: { ...data, status } },
      {
        onSuccess: () => {
          triggerToast(
            `Purchase Order: (${PODetails?.po_number}) updated successfully`,
            "success",
          );

          navigate(
            pathConstants.INVENTORY_PURCHASE_ORDER_DETAILS({
              id: PODetails?.id,
            }),
          );
        },
      },
    );
  };

  const createOrUpdatePO = (status = "IN_REVIEW") => {
    if (isEditMode) handleUpdatePO(status);
    else handleCreatePO(status);
  };

  return {
    isSubmittingPO: isCreatingPO || isUpdatingPO,
    pOForm,
    setPOForm,
    createOrUpdatePO,
    navigate,
  };
};
